import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { APIService } from 'app/services/api.service';
import { SnackbarService } from 'app/services/snackbar.service';
import { Configuration } from 'app/models/configuration.model';
import {
  UserAction,
  UserActionEntityTypeEnum,
  UserActionFilter
} from 'app/models/user-actions.model';
import { utcToLocalFormat } from 'app/shared/pipes/utc-to-local.pipe';
import {
  JsonDiffModalComponent,
  JsonDiffModalData
} from '../json-diff-modal/json-diff-modal.component';

@Component({
  selector: 'app-logs-configuration-modal',
  templateUrl: './logs-configuration-modal.component.html',
  styleUrls: ['./logs-configuration-modal.component.scss']
})
export class LogsConfigurationModalComponent implements OnInit {
  columnsToDisplay = ['select', 'userName', 'actionType', 'actionTimeUTC'];
  dataSource = new MatTableDataSource<UserAction>([]);
  dateFormat: string = utcToLocalFormat;

  pageIndex = 0;
  pageSize = 100;
  totalItems = 0;

  selectedAction: UserAction | null = null;
  isRollbackInProgress = false;
  isLoading = false;

  constructor(
    public dialogRef: MatDialogRef<LogsConfigurationModalComponent>,
    @Inject(MAT_DIALOG_DATA) public configuration: Configuration,
    private dialog: MatDialog,
    private apiService: APIService,
    private snackbar: SnackbarService,
    private translate: TranslateService
  ) {
  }

  async ngOnInit(): Promise<void> {
    await this.loadHistory();
  }

  getColumnHeaderName(column: string): string {
    switch (column) {
      case 'userName':
        return this.translate.instant('USER_ACTIONS.USER_NAME');
      case 'actionType':
        return this.translate.instant('USER_ACTIONS.ACTION_TYPE');
      case 'actionTimeUTC':
        return this.translate.instant('USER_ACTIONS.ACTION_TIME_UTC');
      default:
        return '';
    }
  }

  async handlePageEvent(pageEvent: PageEvent): Promise<void> {
    this.pageIndex = this.pageSize !== pageEvent.pageSize ? 0 : pageEvent.pageIndex;
    this.pageSize = pageEvent.pageSize;
    await this.loadHistory();
  }

  selectAction(action: UserAction): void {
    this.selectedAction = action;
  }

  showDiff(): void {
    if (!this.selectedAction) {
      return;
    }
    this.dialog.open(JsonDiffModalComponent, {
      data: {
        from: this.selectedAction.from,
        to: this.selectedAction.to
      } as JsonDiffModalData
    });
  }

  async rollback(): Promise<void> {
    if (!this.selectedAction || this.isRollbackInProgress) {
      return;
    }

    this.isRollbackInProgress = true;
    try {
      const response = await firstValueFrom(
        this.apiService.rollbackConfiguration(this.configuration.id, this.selectedAction.id));

      if (response.error) {
        this.snackbar.error(response.error.message);
        return;
      }

      this.snackbar.success(this.translate.instant('CONFIGURATION.LOGS.SUCCESS'));
      this.dialogRef.close(response.result);
    } finally {
      this.isRollbackInProgress = false;
    }
  }

  exit(): void {
    this.dialogRef.close();
  }

  private async loadHistory(): Promise<void> {
    const filter: UserActionFilter = {
      entityType: UserActionEntityTypeEnum.Configuration,
      entityId: this.configuration.id,
      pageIndex: this.pageIndex + 1,
      pageSize: this.pageSize
    };

    this.isLoading = true;
    try {
      const response = await this.apiService.getUserActionsHistory(filter);
      if (!response.success) {
        return;
      }

      this.dataSource = new MatTableDataSource(response.data.items);
      this.totalItems = response.data.totalItems;

      // сохраняем выбор при перелистывании страниц
      if (this.selectedAction) {
        this.selectedAction = response.data.items.find(item => item.id === this.selectedAction.id) ?? null;
      }
    } finally {
      this.isLoading = false;
    }
  }
}
